'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { Loader2 } from 'lucide-react';
import { useInView } from 'react-intersection-observer';

import { cn } from '../lib/utils/cn';
import { Button } from '../shadcn/button';

/**
 * Result shape returned by the `loadMore` callback.
 */
export interface LoadMoreResult<T> {
  items: T[];
  hasMore: boolean;
}

/**
 * Props for InfiniteScrollList.
 *
 * Renders extra items loaded after the initial server-rendered page.
 * The initial items are rendered by the parent (e.g. a server component grid).
 * This component owns the "load more" trigger and the appended items grid.
 *
 * When filters change, remount this component with a new `key` prop
 * (e.g. `key={JSON.stringify(filters)}`) to reset all internal state.
 */
export interface InfiniteScrollListProps<T> {
  /**
   * Items rendered by the initial server page (SSR page 0). When provided,
   * they render in the SAME grid container as the later-loaded items
   * (`[...initialItems, ...loadedItems]`), so a responsive CSS grid back-fills
   * across the boundary instead of leaving a ragged last row between two
   * separate grid containers. When omitted, only the loaded items render (the
   * original behaviour) and the parent renders its own initial grid.
   *
   * Pair with `initialItemKeys` (typically `initialItems.map(getItemKey)`) so a
   * loaded page that re-includes an SSR item does not render it twice.
   */
  initialItems?: T[];

  /**
   * Keys of items already rendered by the parent server component.
   * Used for deduplication — items with these keys won't be rendered again.
   * If not provided, no deduplication of initial items is performed.
   */
  initialItemKeys?: string[];

  /** Whether more items exist beyond the initial server page. */
  initialHasMore: boolean;

  /**
   * Async function to load the next page. Receives the 1-indexed page number.
   * Must return `{ items: T[], hasMore: boolean }`.
   * Typically wraps a server action and normalizes its response shape.
   */
  loadMore: (page: number) => Promise<LoadMoreResult<T>>;

  /**
   * Render a single item. The caller must provide a `key` on the returned element.
   * Mutually exclusive with `renderItems`.
   */
  renderItem?: (item: T, index: number) => React.ReactNode;

  /**
   * Render all loaded items at once (for components that need the full array,
   * e.g. grouped/sectioned lists). Mutually exclusive with `renderItem`.
   */
  renderItems?: (items: T[]) => React.ReactNode;

  /** Extract a unique key for deduplication. */
  getItemKey: (item: T) => string;

  /** CSS classes for the items grid/list container. */
  gridClassName?: string;

  /** Inline styles for the grid container. */
  gridStyle?: React.CSSProperties;

  /**
   * Trigger mode:
   * - `'button'` (default): manual "Load more" button
   * - `'auto'`: IntersectionObserver triggers load when sentinel is near viewport
   */
  trigger?: 'button' | 'auto';

  /** Accessible label for the feed region. */
  ariaLabel: string;

  /** Custom button label (ReactNode). Defaults to "Load more". */
  loadMoreLabel?: React.ReactNode;

  /** Content shown while loading the next page (e.g. skeleton cards). */
  loadingSkeleton?: React.ReactNode;

  /** Content shown when all items are loaded. Defaults to "You've reached the end". */
  endOfListMessage?: React.ReactNode;

  /**
   * Content shown in place of the items while there are none (e.g. an empty
   * state). Shown alongside the load-more control, so a first page that is
   * empty but has more to load still explains itself. Omitted: render nothing.
   */
  emptyContent?: React.ReactNode;

  /**
   * Message shown when a load attempt fails after auto-retries are
   * exhausted. Rendered above the retry control. Defaults to a generic
   * "Couldn't load more" line.
   */
  errorMessage?: React.ReactNode;

  /** Custom label for the retry button shown after a failed load. Defaults to "Try again". */
  retryLabel?: React.ReactNode;

  /** Additional className on the root wrapper. */
  className?: string;

  /**
   * IntersectionObserver root margin for auto trigger.
   * Start loading before sentinel enters viewport.
   * @default '200px'
   */
  rootMargin?: string;

  /**
   * Max silent auto-retries on a failed load (auto trigger only) before
   * surfacing the manual retry control. A single transient failure must
   * never permanently end the list, so this is never 0 in practice.
   * @default 2
   */
  maxAutoRetries?: number;
}

export function InfiniteScrollList<T>({
  initialItems,
  initialItemKeys,
  initialHasMore,
  loadMore,
  renderItem,
  renderItems,
  getItemKey,
  gridClassName,
  gridStyle,
  trigger = 'button',
  ariaLabel,
  loadMoreLabel,
  loadingSkeleton,
  endOfListMessage,
  emptyContent,
  errorMessage,
  retryLabel,
  className,
  rootMargin = '200px',
  maxAutoRetries = 2,
}: InfiniteScrollListProps<T>) {
  const [extraItems, setExtraItems] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [page, setPage] = useState(1);
  // `error` is intentionally SEPARATE from `hasMore`. A failed load must
  // never be conflated with "reached the end" — doing so unmounts the
  // sentinel and permanently kills the list on the first transient blip
  // (the original bug). On error we keep `hasMore` true and surface a
  // recoverable retry path instead.
  const [error, setError] = useState(false);
  const [isPending, startTransition] = useTransition();

  // Consecutive-failure counter for the CURRENT page. Reset on any success.
  // Drives bounded silent auto-retry before the manual retry control shows.
  const failedAttempts = useRef(0);

  // Deduplication set — tracks all known keys
  const seenKeys = useRef<Set<string>>(new Set(initialItemKeys ?? []));

  // Ref to the latest loader so a scheduled auto-retry always calls the
  // current closure (avoids a stale-page capture in the retry timer).
  const handleLoadMoreRef = useRef<() => void>(() => undefined);

  // Pending auto-retry backoff timer. Tracked so it can be cleared on
  // unmount — otherwise a filter remount (`key={...}`) or route change
  // mid-backoff fires the timer after unmount, issuing an orphaned
  // server action and a state update on an unmounted component.
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLoadMore = useCallback(() => {
    // Explicit in-flight guard (not just render-gating of the controls) so
    // any programmatic caller of this shared primitive can't stack loads.
    if (isPending) return;
    startTransition(async () => {
      try {
        const result = await loadMore(page);

        // Success — clear any prior error/backoff state.
        failedAttempts.current = 0;
        setError(false);

        if (result.items.length > 0) {
          // Deduplicate
          const newItems = result.items.filter(
            (item) => !seenKeys.current.has(getItemKey(item)),
          );
          newItems.forEach((item) => seenKeys.current.add(getItemKey(item)));

          setExtraItems((prev) => [...prev, ...newItems]);
          setHasMore(result.hasMore);
          setPage((prev) => prev + 1);
        } else {
          // Genuine end-of-list: a SUCCESSFUL resolve that returned no
          // items. Only this path (and an explicit `hasMore: false`) ends
          // the list — never the catch below.
          setHasMore(false);
        }
      } catch {
        // A rejected loadMore is RETRYABLE, never end-of-list. Do NOT touch
        // `hasMore`. Auto-retry the same page a bounded number of times,
        // then surface the manual retry control.
        //
        // INVARIANT: `failedAttempts` counts consecutive failures of the
        // CURRENT page. It is reset ONLY on success and on manual retry —
        // never at the top of handleLoadMore, because handleLoadMore IS the
        // auto-retry entry point (the timer below calls it); resetting there
        // would defeat the cap and loop forever on a persistent failure.
        failedAttempts.current += 1;
        if (trigger === 'auto' && failedAttempts.current <= maxAutoRetries) {
          const backoffMs = 500 * failedAttempts.current;
          retryTimer.current = setTimeout(
            () => handleLoadMoreRef.current(),
            backoffMs,
          );
        } else {
          setError(true);
        }
      }
    });
  }, [page, loadMore, getItemKey, trigger, maxAutoRetries, isPending]);

  // Clear any pending backoff timer on unmount so it can't fire after the
  // component is gone (filter remount / route change mid-backoff).
  useEffect(
    () => () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    },
    [],
  );

  // Keep the retry-timer's loader reference current without touching the ref
  // during render (react-hooks/refs).
  useEffect(() => {
    handleLoadMoreRef.current = handleLoadMore;
  }, [handleLoadMore]);

  // Manual retry: clear error + attempt counter, re-issue the same page.
  const handleRetry = useCallback(() => {
    failedAttempts.current = 0;
    setError(false);
    handleLoadMore();
  }, [handleLoadMore]);

  // Auto-trigger via IntersectionObserver. Skipped while loading or while an
  // error is surfaced (recovery is then user/auto-retry driven, not scroll).
  const { ref: sentinelRef, inView } = useInView({
    rootMargin,
    skip: trigger !== 'auto' || !hasMore || isPending || error,
  });

  useEffect(() => {
    if (trigger === 'auto' && inView && hasMore && !isPending && !error) {
      handleLoadMore();
    }
  }, [trigger, inView, hasMore, isPending, error, handleLoadMore]);

  // SSR page-0 items and the appended items share ONE grid container so the
  // responsive grid back-fills every row. When `initialItems` is omitted this
  // collapses to `extraItems` — identical to the pre-`initialItems` behaviour.
  // `seenKeys` only knows the page 0 present at mount: a router.refresh() with
  // the same key can bring an already-loaded item into `initialItems`, so drop
  // it from the appended tail rather than render it (and its key) twice.
  const allItems = useMemo(() => {
    if (!initialItems) return extraItems;
    const initialKeys = new Set(initialItems.map(getItemKey));
    return [
      ...initialItems,
      ...extraItems.filter((item) => !initialKeys.has(getItemKey(item))),
    ];
    // `getItemKey` is a pure key extractor that consumers pass inline, so its
    // identity changes every render while its output does not. Depending on
    // it would hand renderItems a new array each render (e.g. a DataTable
    // rebuilding its row model on every selection click).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems, extraItems]);

  const itemsContent = useMemo(() => {
    if (allItems.length === 0) return emptyContent ?? null;
    if (renderItems) return renderItems(allItems);
    if (renderItem) {
      return (
        <div className={gridClassName} style={gridStyle}>
          {allItems.map((item, index) => renderItem(item, index))}
        </div>
      );
    }
    return null;
  }, [
    allItems,
    renderItem,
    renderItems,
    gridClassName,
    gridStyle,
    emptyContent,
  ]);

  return (
    <div
      className={cn('space-y-6', className)}
      role="feed"
      aria-label={ariaLabel}
      aria-busy={isPending}
    >
      {/* Loaded items */}
      {itemsContent}

      {/* Loading indicator */}
      {isPending &&
        (loadingSkeleton ?? (
          <div className="flex justify-center py-4" role="status">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            <span className="sr-only">Loading more items...</span>
          </div>
        ))}

      {/* Load-more button (button trigger mode) */}
      {trigger === 'button' && hasMore && !isPending && !error && (
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleLoadMore}
            disabled={isPending}
          >
            {loadMoreLabel ?? 'Load more'}
          </Button>
        </div>
      )}

      {/* Error + retry (both trigger modes). hasMore is untouched, so a
          successful retry resumes paging exactly where it stopped. */}
      {error && !isPending && (
        <div className="flex flex-col items-center gap-2 pt-2" role="alert">
          <p className="text-muted-foreground text-sm">
            {errorMessage ?? "Couldn't load more. Check your connection."}
          </p>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleRetry}
          >
            {retryLabel ?? 'Try again'}
          </Button>
        </div>
      )}

      {/* Sentinel (auto trigger mode). Removed while an error is surfaced so
          recovery flows through the retry control, not the observer. */}
      {trigger === 'auto' && hasMore && !error && (
        <div ref={sentinelRef} className="h-10" aria-hidden="true" />
      )}

      {/* End of list */}
      {!hasMore && extraItems.length > 0 && (
        <div className="pt-2 text-center">
          {endOfListMessage ?? (
            <p className="text-muted-foreground text-sm">
              You&apos;ve reached the end
            </p>
          )}
        </div>
      )}

      {/* Screen reader status */}
      <div className="sr-only" role="status" aria-live="polite">
        {isPending ? 'Loading more items...' : null}
      </div>
    </div>
  );
}
